"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Shield, Lock, Award, Users, Sparkles, Quote } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { AnimatedStat } from "@/components/ui/animated-stat";

const STATS = [
  { value: 300, suffix: "+", label: "Elite Clients", icon: Users, cls: "text-purple", bg: "from-purple/15" },
  { value: 30, suffix: "+", label: "Client Countries", icon: Sparkles, cls: "text-lime", bg: "from-lime/15" },
  { value: 5, suffix: "", label: "Sensitivity Levels", icon: Lock, cls: "text-indigo", bg: "from-indigo/15" },
  { value: 0, suffix: "", label: "Breaches in 15+ Yrs", icon: Shield, cls: "text-emerald", bg: "from-emerald/15" },
  { value: 100, suffix: "%", label: "Compartmentalized", icon: Award, cls: "text-denim", bg: "from-denim/15" },
  { value: 24, suffix: "/7", label: "Operational Silence", icon: Lock, cls: "text-amber-gold", bg: "from-amber-gold/15" },
];

export function CircleOfTrustSection() {
  return (
    <Section id="circle-of-trust" ariaLabel="The Circle of Trust" background="muted">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
        <div>
          <SectionHeader
            icon={Shield}
            overline="The Circle of Trust"
            overlineClassName="text-emerald"
            pillClassName="glass-tinted-emerald border-emerald/20"
            align="left"
            title={
              <>
                Elite Clientele. <br />
                <span className="text-gradient-success">Absolute Discretion.</span>
              </>
            }
          />
          <div className="relative glass-strong rounded-2xl p-8 gradient-border">
            <Quote className="size-10 text-red/30 absolute -top-3 -left-3" />
            <p className="text-body-lg italic leading-relaxed mb-5">
              &ldquo;Zero security incidents. Zero breaches. Zero leaks. Fifteen-plus years. This is the
              track record that permits the work to continue at the level where it operates.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-gradient-success flex items-center justify-center">
                <Lock className="size-5 text-white" />
              </div>
              <div>
                <div className="text-overline text-emerald">Standard of Confidentiality</div>
                <div className="text-caption text-muted-foreground">National-security level NDAs</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className={`glass-card rounded-xl p-4 bg-gradient-to-br ${s.bg} to-transparent group`}
            >
              <s.icon className={`size-5 ${s.cls} mb-2.5 group-hover:scale-110 transition-transform`} />
              <div className={`text-display-sm font-bold ${s.cls} leading-none mb-1.5`}>
                <AnimatedStat value={s.value} suffix={s.suffix} />
              </div>
              <div className="text-caption text-muted-foreground font-semibold tracking-wide uppercase">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}
